import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import certificationCatalog from "../Data/Certdata";
import defaultCert from "../Components/cert.png";

const AllCertifications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ 1. Initialize state with filter from Navbar if it exists
  const [searchQuery, setSearchQuery] = useState("");
  const [activeVendor, setActiveVendor] = useState(location.state?.filter || "All");

  // ✅ 2. Effect to update filter if Navbar is clicked while already on this page
  useEffect(() => {
    if (location.state?.filter) {
      setActiveVendor(location.state.filter);
      // Optional: Clear search query when a specific vendor is selected from Navbar
      setSearchQuery(""); 
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location.state]);

  // ✅ 3. Optimized filtering logic
  const filteredCerts = useMemo(() => {
    return certificationCatalog.filter((vendor) => {
      // Check if current vendor matches the selected dropdown/navbar vendor
      const matchesVendor = activeVendor === "All" || 
        vendor.vendorName.toLowerCase().includes(activeVendor.toLowerCase());
      
      // Check if name or any internal exam matches search input
      const matchesSearch = 
        vendor.vendorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vendor.certifications.some(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
        
      return matchesVendor && matchesSearch;
    });
  }, [searchQuery, activeVendor]);

  // Generate unique vendor list for dropdown
  const vendorsList = ["All", ...new Set(certificationCatalog.map(v => v.vendorName))];

  return (
    <section className="min-h-screen bg-slate-50 pt-28 pb-20 px-4 md:px-12">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-10 text-center md:text-left">
          <button 
            onClick={() => navigate('/')} 
            className="group text-purple-600 font-bold flex items-center gap-2 mb-6 hover:text-purple-800 transition-colors mx-auto md:mx-0"
          >
            <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Home
          </button>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight">
            {activeVendor === "All" ? "Global Certifications" : `${activeVendor} Exams`}
          </h1>
          <p className="text-slate-500 mt-3 text-lg font-medium">
            Explore <span className="text-purple-600 font-bold">{filteredCerts.length}</span> verified vendors and their career paths.
          </p>
        </div>

        {/* ✅ Search & Filter - Sticky with Glassmorphism */}
        <div className="sticky top-24 z-40 bg-white/70 backdrop-blur-xl p-3 md:p-4 rounded-[2rem] border border-white shadow-2xl shadow-purple-100/50 mb-12 flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
            <input 
              type="text"
              placeholder="Search by vendor (e.g. AWS) or exam (e.g. PMP)..."
              value={searchQuery}
              className="w-full pl-12 pr-4 py-4 rounded-2xl bg-slate-100/50 border-none focus:ring-2 focus:ring-purple-500 outline-none transition-all text-slate-700 font-medium"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <select 
              className="flex-grow md:w-64 px-4 py-4 rounded-2xl bg-slate-100/50 border-none outline-none font-bold text-slate-700 cursor-pointer focus:ring-2 focus:ring-purple-500"
              value={activeVendor}
              onChange={(e) => setActiveVendor(e.target.value)}
            >
              {vendorsList.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            {(activeVendor !== "All" || searchQuery !== "") && (
              <button 
                onClick={() => {setActiveVendor("All"); setSearchQuery("");}}
                className="px-6 py-4 rounded-2xl bg-purple-100 text-purple-700 font-bold hover:bg-purple-200 transition-colors"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* ✅ Grid Display */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredCerts.map((vendor) => (
            <div
              key={vendor.vendorId}
              onClick={() => navigate(`/certificates/${vendor.vendorId}`)}
              className="group relative bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-purple-200/40 transition-all duration-500 cursor-pointer overflow-hidden flex flex-col items-center"
            >
              {/* Animated background accent */}
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-slate-50 rounded-full group-hover:bg-purple-50 transition-colors duration-500"></div>

              {/* Vendor Logo */}
              <div className="relative w-24 h-24 mb-6 flex items-center justify-center p-3 bg-white rounded-3xl shadow-inner group-hover:scale-110 transition-transform duration-500">
                <img
                  src={vendor.vendorLogo || defaultCert}
                  alt={vendor.vendorName}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultCert;
                  }}
                />
              </div>

              <h3 className="text-xl font-bold text-slate-800 text-center group-hover:text-purple-700 transition-colors">
                {vendor.vendorName}
              </h3>
              <p className="text-slate-500 text-sm text-center mt-3 line-clamp-2 leading-relaxed">
                {vendor.vendorDescription}
              </p>

              {/* Card Footer */}
              <div className="mt-8 w-full pt-6 border-t border-slate-50 flex items-center justify-between">
                <div className="flex flex-col">
                   <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Exams</span>
                   <span className="text-xl font-black text-slate-800">{vendor.certifications.length}</span>
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center group-hover:bg-purple-600 group-hover:translate-x-1 transition-all">
                  →
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredCerts.length === 0 && (
          <div className="text-center py-24 bg-white rounded-[3rem] border border-dashed border-slate-200">
            <div className="text-6xl mb-4">🕵️‍♂️</div>
            <h3 className="text-2xl font-bold text-slate-800">No matches found</h3>
            <p className="text-slate-500 mt-2">Try adjusting your search or category filters.</p>
            <button 
              onClick={() => {setActiveVendor("All"); setSearchQuery("");}}
              className="mt-6 text-purple-600 font-bold hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default AllCertifications;